import type {CreateReceiptRequest} from '@rocket-ui/lk-api';
import {
	parseReceiptDateToIso,
	parseReceiptPriceCents,
	sumReceiptServicesCents,
	type ReceiptClientType,
	type ReceiptServiceItem,
} from './receiptForm';

export type BuildReceiptRequestInput = {
	date: string;
	services: ReceiptServiceItem[];
	clientType: ReceiptClientType;
	isForeignOrganization: boolean;
	inn: string;
	organizationName: string;
};

function isInnValid(inn: string): boolean {
	return /^\d{10}$|^\d{12}$/.test(inn.trim());
}

export function isReceiptFormComplete(input: BuildReceiptRequestInput): boolean {
	if (!parseReceiptDateToIso(input.date) || input.services.length === 0) {
		return false;
	}

	const hasInvalidService = input.services.some((service) => {
		return !service.name.trim() || parseReceiptPriceCents(service.price) <= 0;
	});

	if (hasInvalidService) {
		return false;
	}

	if (input.clientType === 'legal' && input.isForeignOrganization) {
		return isInnValid(input.inn) && Boolean(input.organizationName.trim());
	}

	return true;
}

export function buildReceiptRequest(input: BuildReceiptRequestInput): CreateReceiptRequest | null {
	if (!isReceiptFormComplete(input)) {
		return null;
	}

	const paidAt = parseReceiptDateToIso(input.date);

	if (!paidAt) {
		return null;
	}

	const services = input.services.map((service) => ({
		name: service.name.trim(),
		amountCents: parseReceiptPriceCents(service.price),
		quantity: 1,
	}));

	const amountCents = sumReceiptServicesCents(input.services);
	const isLegal = input.clientType === 'legal';

	const request: CreateReceiptRequest = {
		payeeType: isLegal ? 'legal_entity' : 'individual',
		amountCents,
		paidAt,
		services,
	};

	if (isLegal) {
		request.foreignPayee = input.isForeignOrganization;

		const inn = input.inn.trim();
		const payeeName = input.organizationName.trim();

		if (inn) {
			request.payeeInn = inn;
		}

		if (payeeName) {
			request.payeeName = payeeName;
		}
	}

	return request;
}

export function canSubmitReceipt(input: BuildReceiptRequestInput): boolean {
	return isReceiptFormComplete(input);
}
